import React from 'react';
import { Input } from '@oysterjs/ui/input';
import { Listbox, ListboxDescription, ListboxLabel, ListboxOption } from '@oysterjs/ui/listbox';
import { TriggerEventType } from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { BuilderField } from '~/components/triggers/email-builder/fields';
import { CreationControls } from '~/components/triggers/email-builder/creation-controls';
import { CreationSectionCard } from '~/components/triggers/email-builder/creation-card';

export const CREATION_EVENTS = [
  {
    value: 'mailbox',
    title: 'Mailbox email',
    description: 'An email arrives in a connected Outlook mailbox',
    eventType: TriggerEventType.EmailReceived
  },
  {
    value: 'alias',
    title: 'Forwarded email',
    description: 'An email is forwarded or BCC’d to a Cara address',
    eventType: TriggerEventType.EmailReceived
  },
  {
    value: 'schedule',
    title: 'Schedule',
    description: 'Selected days of the week at a set time',
    eventType: TriggerEventType.ScheduleDue
  },
  {
    value: 'call',
    title: 'Call',
    description: 'A call ends and has been analyzed',
    eventType: TriggerEventType.CallAnalyzed
  }
] as const;
export type CreationEvent = (typeof CREATION_EVENTS)[number]['value'];
export type CreationEventGuard = { dirty: boolean; locked: boolean };
export const CreationEventContext = React.createContext<{
  value: CreationEvent;
  onChange: (event: CreationEvent) => void;
  guard: CreationEventGuard;
  setGuard: React.Dispatch<React.SetStateAction<CreationEventGuard>>;
  // Set by a switch; the picker in the new editor takes focus back once it mounts.
  refocus?: React.MutableRefObject<boolean>;
  // The name belongs to the page, not to an event's editor, so it survives a switch and is
  // there while an editor is still loading or unavailable.
  name: string;
  setName: (name: string) => void;
  nameError?: string;
  setNameError: (error?: string) => void;
} | null>(null);

// Switching starts a new draft, never converts one event's matcher into another.
export const useCreationEventGuard = (dirty: boolean, locked: boolean) => {
  const setGuard = React.useContext(CreationEventContext)?.setGuard;
  React.useEffect(() => {
    setGuard?.({ dirty, locked });
    return () => setGuard?.({ dirty: false, locked: false });
  }, [dirty, locked, setGuard]);
};
// The editors report their own name validation here so the message sits on the page's field.
export const useCreationNameError = (error?: string) => {
  const setNameError = React.useContext(CreationEventContext)?.setNameError;
  React.useEffect(() => {
    setNameError?.(error);
    return () => setNameError?.(undefined);
  }, [error, setNameError]);
};

export const CreationNameField = () => {
  const event = React.useContext(CreationEventContext);
  if (!event) return null;
  return (
    <div className="rounded-lg bg-white shadow dark:bg-neutral-800">
      <div className="px-4 py-5 sm:px-6">
        <BuilderField
          label="Trigger name"
          path="name"
          errors={event.nameError ? { name: event.nameError } : {}}
          description="A short label shown in the triggers list. Starts monitoring on creation."
        >
          {(props) => (
            <Input
              {...props}
              className="max-w-md"
              value={event.name}
              disabled={event.guard.locked}
              onChange={(change) => event.setName(change.target.value)}
            />
          )}
        </BuilderField>
      </div>
    </div>
  );
};

export const CreationEventField = () => {
  const hideMailbox = useFlag(FEATURE_FLAGS.HIDE_MAILBOX_TRIGGER_EVENT);
  const event = React.useContext(CreationEventContext);
  const wrapper = React.useRef<HTMLDivElement>(null);
  const refocus = event?.refocus;
  React.useLayoutEffect(() => {
    if (!refocus?.current) return;
    refocus.current = false;
    wrapper.current?.querySelector('button')?.focus({ preventScroll: true });
  }, [refocus]);
  if (!event) return null;
  return (
    <div ref={wrapper} className="max-w-md">
      <BuilderField label="Event" path="eventType" errors={{}}>
        {() => (
          <Listbox
            value={event.value}
            disabled={event.guard.locked}
            onChange={(value: CreationEvent) => event.onChange(value)}
          >
            {CREATION_EVENTS.filter(({ value }) => !hideMailbox || value !== 'mailbox').map(
              ({ value, title, description }) => (
                <ListboxOption key={value} value={value}>
                  <ListboxLabel>{title}</ListboxLabel>
                  <ListboxDescription>{description}</ListboxDescription>
                </ListboxOption>
              )
            )}
          </Listbox>
        )}
      </BuilderField>
    </div>
  );
};
export const CreationEventLoadState = ({ children }: React.PropsWithChildren) => {
  const event = React.useContext(CreationEventContext);
  if (!event) return <>{children}</>;
  return (
    <CreationControls.Provider value>
      <div className="email-creation-flow">
        <CreationSectionCard step="when">
          <div className="space-y-4">
            <CreationEventField />
            {children}
          </div>
        </CreationSectionCard>
      </div>
    </CreationControls.Provider>
  );
};
